import {
  AlertDialog,
  Button,
  ButtonGroup,
  DeleteIcon,
  EditIcon,
  Modal,
  Row,
  Table,
  TableColumns,
} from '@bp/ui-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import {
  PimAllAuthClientsDocument,
  PimAllAuthClientsQuery,
  PimAllAuthClientsQueryVariables,
  PimDeleteAuthClientsDocument,
} from '../../client/graphql-client-defs';
import { AuthClientForm } from './Form/AuthClientForm';
import { AuthClientResponse } from './graphql/types';

export const AuthClients = () => {
  const { t } = useTranslation();
  const context = useMemo(() => ({ additionalTypenames: ['AuthClients'] }), []);

  const [{ data }] = useQuery<PimAllAuthClientsQuery, PimAllAuthClientsQueryVariables>({
    query: PimAllAuthClientsDocument,
    context,
  });

  const [, deleteAuthClient] = useMutation(PimDeleteAuthClientsDocument);

  const [openForm, setOpenForm] = useState(false);
  const [currentAuthClient, setCurrentAuthClient] = useState<string | null>(null);

  const createColumns = useMemo((): TableColumns<AuthClientResponse>[] => {
    return [
      {
        header: t('authClients.clientName') as string,
        accessorKey: 'clientName',
        id: 'clientName',
        type: 'string',
        size: 250,
      },
      {
        header: t('authClients.clientId') as string,
        accessorKey: 'clientId',
        id: 'clientId',
        type: 'string',
        size: 250,
        canExpand: true,
      },
      {
        header: t('authClients.redirectUris') as string,
        accessorKey: 'redirectUris',
        id: 'redirectUris',
        size: 500,
      },
    ];
  }, [t]);

  const tableData = useMemo(() => {
    return data?.authClients ?? [];
  }, [data]);

  const handleClose = () => {
    setOpenForm(false);
    setCurrentAuthClient(null);
  };

  const handleEdit = (row: Row<AuthClientResponse>) => {
    setCurrentAuthClient(row.original.clientId);
    setOpenForm(true);
  };

  const handleDelete = (row: Row<AuthClientResponse>) => {
    deleteAuthClient({ clientId: row.original.clientId }, context);
  };
  useEffect(() => {
    document.title = `AuthClients`;
  }, []);

  return (
    <div className='page'>
      <Table<AuthClientResponse>
        showActionBar
        isOnWhite={false}
        actionBarSettings={{ showAddButton: true }}
        data={tableData}
        columns={createColumns}
        onAddClick={() => {
          setOpenForm(true);
        }}
        lastCol={(row) => (
          <ButtonGroup>
            <Button
              hierarchy='secondary'
              onClick={() => handleEdit(row)}
              icon={<EditIcon className={'svg-icon'} />}
            ></Button>
            <AlertDialog
              title={t('delete.title')}
              confirmText={t('delete.confirmText')}
              cancelText={t('delete.abort')}
              onConfirm={() => handleDelete(row)}
              trigger={<Button hierarchy='secondary' icon={<DeleteIcon className={'svg-icon'} />} />}
            >
              <p>{t('delete.confirmText')}</p>
            </AlertDialog>
          </ButtonGroup>
        )}
        lastColWidth={'150px'}
      />
      {openForm && (
        <Modal
          isOpen={openForm}
          onRequestClose={handleClose}
          title={currentAuthClient ? (t('authClients.edit') as string) : (t('authClients.create') as string)}
          subtitle={currentAuthClient ?? ''}
        >
          <AuthClientForm closeForm={handleClose} clientId={currentAuthClient}></AuthClientForm>
        </Modal>
      )}
    </div>
  );
};
